import { Translator, getLangFromUrl } from 'Translator';
import ErrorBoundary from 'components/ErrorBoundary';
import App from 'modules/app';
import React, { PureComponent, ReactNode } from 'react';
import { Toaster } from 'react-hot-toast';
import {
  Redirect,
  Route,
  RouteComponentProps,
  Router,
  Switch,
  useLocation,
} from 'react-router-dom';
import {
  convertPathToHashBased,
  generatePathnameWithLangCode,
  setCorrectLocaleParam,
} from 'utils/RouteHelper';
import { TrekkerProvider } from 'utils/analytics';
import basePath from 'utils/basePath';
import { isCordova } from 'utils/platform';
import useAuthStore from '~/authentication/store';
import { useDeviceInfoStore } from '~/stores/device';
import { useI18n } from '~/stores/i18n';

function Analytics({ children }: { children: ReactNode }) {
  const location = useLocation();
  const accountId = useAuthStore((state) => state.account?.id);
  const anonymousDeviceId = useDeviceInfoStore(
    (state) => state.anonymousDeviceId,
  );

  return (
    <TrekkerProvider
      context={{
        path: location.pathname,
        queryString: location.search,
        account: accountId ? { id: accountId } : undefined,
        anonymousDeviceId,
      }}
    >
      {children}
    </TrekkerProvider>
  );
}

class Root extends PureComponent<RouteComponentProps<{ locale: string }>> {
  setLocale: boolean;
  unsubscribeLang: null | (() => void);

  getLocale = () => {
    if (this.props.match.params.locale) {
      return this.props.match.params.locale;
    }

    if (getLangFromUrl()) {
      return getLangFromUrl();
    }
    const realAccount = useAuthStore.getState().realAccount;
    return realAccount?.preferredLang;
  };

  componentDidMount() {
    convertPathToHashBased(this.props);

    const locale = this.getLocale();
    // Keep: set language when URL param differs
    if (!!locale && Translator.getLanguageCode() !== locale) {
      Translator.setLanguageCode(locale);
    }

    this.unsubscribeLang = useI18n.subscribe((state, prevState) => {
      if (state.lang === prevState.lang) return;
      const preferredLang = state.lang;
      const shouldHandle =
        (!this.props.match.params.locale && preferredLang === 'en') ||
        (this.props.match.params.locale !== preferredLang && !!preferredLang);
      if (!shouldHandle) return;
      const newPathname = generatePathnameWithLangCode(
        this.props.location.pathname,
        preferredLang,
      );
      if (newPathname === this.props.location.pathname) return;
      if (isCordova()) return;
      if (!this.setLocale) {
        this.props.history.replace({
          ...this.props.location,
          pathname: newPathname,
        });
        window.location.reload();
      }
    });
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.match.params.locale !== this.props.match.params.locale) {
      this.setLocale = setCorrectLocaleParam(nextProps);
    }
  }

  componentWillUnmount() {
    if (this.unsubscribeLang) {
      this.unsubscribeLang();
    }
  }

  render() {
    return (
      <ErrorBoundary>
        <Analytics>
          <App />
        </Analytics>
      </ErrorBoundary>
    );
  }
}

const addTrailingSlash = (
  props: RouteComponentProps<
    { locale: string; 'url*': string },
    {},
    { modal?: any }
  >,
) => {
  if (props.location?.state?.modal) {
    return <Root {...props} />;
  }
  const [pathnameWithoutQuery, ...query] = props.location.pathname.split('?');
  if (pathnameWithoutQuery.endsWith('/')) {
    return <Root {...props} />;
  }
  const queryCombined = query.join('?');

  if (
    ENVIRONMENT !== 'production' &&
    !pathnameWithoutQuery.endsWith('/') &&
    !pathnameWithoutQuery.endsWith('/index.html')
  ) {
    console.error(`${pathnameWithoutQuery} path should have a trailing slash`);
  }

  const pathnameWithSlash = pathnameWithoutQuery.endsWith('/')
    ? pathnameWithoutQuery
    : `${pathnameWithoutQuery}/`;
  const pathnameFull = `${pathnameWithSlash}${
    queryCombined ? '?' + queryCombined : ''
  }`;
  return (
    <Redirect
      to={{
        ...props.location,
        pathname: pathnameFull,
      }}
    />
  );
};

export default ({ history }) => {
  return (
    <Router history={history}>
      <Switch>
        <Route
          path="/index.html"
          exact
          strict
          render={() => <Redirect to="/" />}
        />
        <Route path="/:url*" exact strict render={addTrailingSlash} />
        <Route path={basePath} component={Root} />
      </Switch>
      <Toaster />
    </Router>
  );
};
